/**
 * Role codes and access helpers aligned with backend `authentication.Role.RoleCode`.
 * Effective role uses the same priority as the API `effective_role` field.
 */

export const RoleCode = {
  SYSTEM_ADMIN: 'SYSTEM_ADMIN',
  SYSTEM_FINANCIAL: 'SYSTEM_FINANCIAL',
  SYSTEM_DEVELOPER: 'SYSTEM_DEVELOPER',
  MERCHANT_ADMIN: 'MERCHANT_ADMIN',
  MERCHANT_FINANCIAL: 'MERCHANT_FINANCIAL',
  MERCHANT_DEVELOPER: 'MERCHANT_DEVELOPER',
} as const;

export type RoleCodeValue = (typeof RoleCode)[keyof typeof RoleCode];

/** Highest-first (must match backend `effective_role_from_codes`). */
export const ROLE_PRIORITY: readonly RoleCodeValue[] = [
  RoleCode.SYSTEM_ADMIN,
  RoleCode.SYSTEM_FINANCIAL,
  RoleCode.SYSTEM_DEVELOPER,
  RoleCode.MERCHANT_ADMIN,
  RoleCode.MERCHANT_FINANCIAL,
  RoleCode.MERCHANT_DEVELOPER,
];

export function effectiveRoleFromCodes(activeCodes: string[]): RoleCodeValue | null {
  if (!activeCodes.length) {
    return null;
  }
  const set = new Set(activeCodes);
  for (const code of ROLE_PRIORITY) {
    if (set.has(code)) {
      return code;
    }
  }
  const firstUnknown = activeCodes.find((c) => !ROLE_PRIORITY.includes(c as RoleCodeValue));
  return (firstUnknown ?? activeCodes[0]) as RoleCodeValue;
}

export function isSystemRoleCode(code: RoleCodeValue | null): boolean {
  if (!code) {
    return false;
  }
  return (
    code === RoleCode.SYSTEM_ADMIN || code === RoleCode.SYSTEM_FINANCIAL || code === RoleCode.SYSTEM_DEVELOPER
  );
}

export function isMerchantRoleCode(code: RoleCodeValue | null): boolean {
  if (!code) {
    return false;
  }
  return (
    code === RoleCode.MERCHANT_ADMIN ||
    code === RoleCode.MERCHANT_FINANCIAL ||
    code === RoleCode.MERCHANT_DEVELOPER
  );
}

/** All defined role codes (for entity allow lists). */
export const ALL_ROLE_CODES: readonly RoleCodeValue[] = [...ROLE_PRIORITY];

/**
 * Whether an entity config allows the effective role.
 * Empty or missing `allowedRoles` means all roles (use sparingly).
 */
export function canAccessWithAllowedRoles(
  allowedRoles: readonly RoleCodeValue[] | undefined,
  effectiveRole: RoleCodeValue | null,
): boolean {
  if (!effectiveRole) {
    return false;
  }
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }
  return allowedRoles.includes(effectiveRole);
}

/** Auth user shape for role resolution (matches API / login payload). */
export interface RoleAwareUser {
  effective_role?: string | null;
  active_roles?: string[];
  active_assignment?: { role_code?: string | null; role_type?: string | null } | null;
}

/**
 * Effective role for UI: prefer token context (`active_assignment` / API parity),
 * then API `effective_role`, else compute from `active_roles`.
 */
export function resolveEffectiveRole(user: RoleAwareUser | null): RoleCodeValue | null {
  if (!user) {
    return null;
  }
  const fromActive = user.active_assignment?.role_code;
  if (fromActive) {
    return fromActive as RoleCodeValue;
  }
  const fromApi = user.effective_role;
  if (fromApi && ROLE_PRIORITY.includes(fromApi as RoleCodeValue)) {
    return fromApi as RoleCodeValue;
  }
  return effectiveRoleFromCodes(user.active_roles ?? []);
}
